import { Link } from 'react-router-dom';

import { defaultLocale, type Locale } from '@sonasi/shared-types';

import { TranslateWithAi } from './TranslateWithAi';
import type { TranslatableKind } from '@/lib/translation';

export interface LanguageTab {
  locale: Locale;
  label: string;
  percent: number;
  translated: number;
  total: number;
}

/**
 * The bar under the header, shared by both editors.
 *
 * v2 moves the language picker out of the page and into a strip of its own that
 * sticks below the header, so the tabs stay reachable however far down the form
 * somebody has scrolled — which is the point of them: a translator works a
 * field at a time and switches language constantly.
 *
 * The page's own title is not repeated here. The header above already carries
 * it with the breadcrumb, and printing it twice was the first thing the v1
 * editors did on every screen.
 */
export function EditorTabs({
  back,
  backLabel,
  tabs,
  current,
  onPick,
  meta,
  label,
  translate,
}: {
  back: string;
  backLabel: string;
  tabs: LanguageTab[];
  current: Locale;
  onPick: (locale: Locale) => void;
  /** What state this record is in, in the design's lower-case mono. */
  meta: string;
  /** Names the tablist for anybody who cannot see it sitting under a title. */
  label: string;
  /**
   * Which record "Translate with AI" would fill, or nothing to leave the
   * button off this bar.
   *
   * ⚠️ Given, not switched on: the button needs the record's kind and id to
   * ask the server for a plan, so a bar that has neither cannot show it. Both
   * editors pass theirs — the design draws the button in the strip they
   * share, and nothing about translating a post differs from an app.
   */
  translate?: { kind: TranslatableKind; id: string };
}) {
  return (
    <div
      className="sticky top-[50px] z-[4] flex flex-wrap items-center gap-2 border-b px-[18px] py-[7px]"
      style={{
        borderColor: 'var(--line)',
        background: 'color-mix(in oklab, var(--panel) 94%, transparent)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <Link
        to={back}
        aria-label={backLabel}
        title={backLabel}
        className="btn-quiet focus-ring cursor-pointer pr-1.5 text-xs"
        style={{ color: 'var(--dim)' }}
      >
        ←
      </Link>

      {/*
        A tablist, not a row of buttons. Each one selects which language the
        form below is showing, which is what a tab is — and it is what a screen
        reader needs to hear to know that picking one changes the fields rather
        than doing something.
      */}
      <span
        role="tablist"
        aria-label={label}
        className="inline-flex rounded-lg border p-0.5"
        style={{ background: 'var(--panel2)', borderColor: 'var(--line)' }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.locale}
            type="button"
            role="tab"
            aria-selected={tab.locale === current}
            onClick={() => onPick(tab.locale)}
            title={`${tab.translated} of ${tab.total} fields written`}
            className="seg focus-ring inline-flex min-h-[26px] cursor-pointer items-baseline gap-1.5 px-2.5 py-[5px] text-[11.5px]"
          >
            <span>{tab.label}</span>
            <span className="font-mono text-[9.5px] opacity-70">{tab.percent}%</span>
          </button>
        ))}
      </span>

      <span className="ml-auto flex items-center gap-2.5">
        {/*
          ⚠️ **Only on the base language, because it is the one being translated
          *from*.** Standing on the 日本語 tab, "Translate" has no answer to
          "from what into what" — the design hides it there and so does this.
          Everything else about the button — the plan before the run, the
          number before the bill — is in `TranslateWithAi`.
        */}
        {translate ? (
          <TranslateWithAi kind={translate.kind} id={translate.id} offered={current === defaultLocale} />
        ) : null}

        <span className="font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
          {meta}
        </span>
      </span>
    </div>
  );
}
